import {normalizePage} from "@/lib/paging.ts";
import api from "@/lib/api.ts";
import {QueryKey, useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {bannerKeys} from "@/hooks/useBanners.ts";

// ───────────────────────────────────────────────────────────────────────────────
// Types
// ───────────────────────────────────────────────────────────────────────────────
export type CategoryId = string;

export type Category = {
    id: CategoryId;
    name: string;
    description?: string | null;
    slug?: string;
};

export type SaveCategoryInput = {
    id?: CategoryId;
    name: string;
    description?: string | null;
};

export type SortField = "name";
export type SortOrder = "asc" | "desc";

export type HookParams = {
    page?: number;
    size?: number;
    sortBy?: SortField;
    sortOrder?: SortOrder;
};

export type CategoryPage = ReturnType<typeof normalizePage<Category>>;

// ───────────────────────────────────────────────────────────────────────────────
// Constants
// ───────────────────────────────────────────────────────────────────────────────
const PUBLIC_BASE = "/api/v1/public/categories";
const EDITOR_BASE = "/api/v1/editor/categories";

const DEFAULTS = {
    page: 0,
    size: 10,
    sortBy: "title" as SortField,
    sortOrder: "desc" as SortOrder,
};

// ───────────────────────────────────────────────────────────────────────────────
// Query Keys
// ───────────────────────────────────────────────────────────────────────────────
export const categoryKeys = {
    all: ["categories"] as const,
    list: (params?: HookParams) => [...categoryKeys.all, "list", params ?? {}] as const,
};

// ───────────────────────────────────────────────────────────────────────────────
// Fetchers
// ───────────────────────────────────────────────────────────────────────────────
function emptyPage(page = DEFAULTS.page, size = DEFAULTS.size): CategoryPage {
    return normalizePage<Category>({page, size, total: 0, content: []});
}

async function fetchCategories(params: HookParams = {}): Promise<CategoryPage> {
    const page = params.page ?? DEFAULTS.page;
    const size = params.size ?? DEFAULTS.size;
    const sortBy = params.sortBy ?? DEFAULTS.sortBy;
    const sortOrder = params.sortOrder ?? DEFAULTS.sortOrder;

    const sort = `${sortBy},${sortOrder}`;
    const {data} = await api.get(PUBLIC_BASE, {params: {page, size, sort}});

    if (Array.isArray(data?.content)) return normalizePage<Category>(data);
    if (Array.isArray(data?.items) && typeof data?.total === "number") return data;
    return emptyPage(page, size);
}

// ───────────────────────────────────────────────────────────────────────────────
// Queries
// ───────────────────────────────────────────────────────────────────────────────
export function useCategories(params: HookParams = {}) {
    const q: HookParams = {
        page: params?.page ?? DEFAULTS.page,
        size: params?.size ?? DEFAULTS.size,
        sortBy: params?.sortBy ?? DEFAULTS.sortBy,
        sortOrder: params?.sortOrder ?? DEFAULTS.sortOrder,
    };

    return useQuery({
        queryKey: categoryKeys.list(q),
        queryFn: () => fetchCategories(q),
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        placeholderData: (prev) => prev,
    });
}

// ───────────────────────────────────────────────────────────────────────────────
// Mutations
// ───────────────────────────────────────────────────────────────────────────────
export function useSaveCategory() {
    const qc = useQueryClient();

    return useMutation({
        mutationKey: [...categoryKeys.all, "save"],
        mutationFn: async (payload: SaveCategoryInput): Promise<Category> => {
            const body = {
                name: payload.name?.trim(),
                description: payload.description?.toString().trim() || undefined,
            };

            if (payload.id) {
                const {data} = await api.put(`${EDITOR_BASE}/${payload.id}`, body);
                return data;
            } else {
                const {data} = await api.post(EDITOR_BASE, body);
                return data;
            }
        },
        onSuccess: () => {
            qc.invalidateQueries({queryKey: categoryKeys.all});
        },
    });
}

export function useDeleteCategory() {
    const qc = useQueryClient();

    return useMutation({
        mutationKey: [...categoryKeys.all, "delete"],
        mutationFn: async (id: CategoryId) => {
            await api.delete(`${EDITOR_BASE}/${id}`);
            return id;
        },
        onMutate: async (id) => {
            // Cancel outgoing refetches so we don't overwrite our optimistic update
            await qc.cancelQueries({queryKey: categoryKeys.all});

            // Snapshot all related queries
            const snapshots = qc.getQueriesData<unknown>({queryKey: categoryKeys.all});

            // Optimistically remove the banner from any paged lists we find
            snapshots.forEach(([key, value]) => {
                const page = value as Partial<CategoryPage> | undefined;
                if (page && Array.isArray(page.items)) {
                    const next: CategoryPage = {
                        ...(page as CategoryPage),
                        items: page.items.filter((b: any) => b.id !== id),
                        total: Math.max(0, (page.total as number) - 1),
                    };
                    qc.setQueryData(key, next);
                }
            });

            return {snapshots};
        },
        onError: (_err, _id, ctx) => {
            // Roll back from snapshots if we have them
            ctx?.snapshots?.forEach(([key, value]: [unknown, unknown]) => {
                qc.setQueryData(<QueryKey>key, value);
            });
        },
        onSettled: () => {
            // Ensure server truth wins in the end
            qc.invalidateQueries({queryKey: bannerKeys.all});
        },
    });
}
